const CWV_METRIC_TYPES = {
  CLS: 'layout-shift',
  LCP: 'largest-contentful-paint',
  FCP: 'paint',
  INP: 'event',
};

const DEFAULT_OPTIONS = {
  debug: false,
  sampleRate: 1,
  disableInThemeEditor: true,
  flushIntervalMs: 5000,
  interactionTtlMs: 5 * 60 * 1000,
  namespace: '__inkposterPerformance',
};

const formatTime = (value) => Number((value || 0).toFixed(2));

const now = () => performance.now();

const cloneSnapshot = (value) => {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
};

const getSectionMetricsKey = (sectionId) => `section:${sectionId}`;

const getShopifySectionId = (element) => {
  if (!element) return null;

  const directSection = element.closest?.('[data-section-id]');

  if (directSection?.dataset?.sectionId) return directSection.dataset.sectionId;

  const shopifySection = element.closest?.('[id^="shopify-section-"]');

  if (!shopifySection?.id) return null;

  return shopifySection.id.replace('shopify-section-', '');
};

const getBlockId = (element) => {
  if (!element) return null;

  const withData = element.closest?.('[data-block-id]');

  if (withData?.dataset?.blockId) return withData.dataset.blockId;

  const blockAttribute = element.closest?.('[id^="Slide-"]');

  if (blockAttribute?.id) return blockAttribute.id;

  const sourceBlockAttribute = element.closest?.('[data-source-anchor-id]');

  if (sourceBlockAttribute?.dataset?.sourceAnchorId) return sourceBlockAttribute.dataset.sourceAnchorId;

  return null;
};

class PerformanceObserverHelper {
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.observers = [];
    this.sectionMetrics = new Map();
    this.blockMetrics = new Map();
    this.pageMetrics = {
      LCP: null,
      CLS: 0,
      INP: null,
      FCP: null,
      TTFB: null,
      longTasksCount: 0,
      longTasksDuration: 0,
    };
    this.interactions = new Map();
    this.flushTimer = null;
    this.lifecycleAbortController = null;
    this.started = false;
  }

  configure(options = {}) {
    const previousFlushIntervalMs = this.options.flushIntervalMs;

    this.options = { ...this.options, ...options };

    if (this.started && previousFlushIntervalMs !== this.options.flushIntervalMs) {
      if (this.flushTimer) {
        window.clearInterval(this.flushTimer);
      }

      this.flushTimer = window.setInterval(() => {
        this.flush('interval');
      }, this.options.flushIntervalMs);
    }
  }

  isSamplingEnabled() {
    return this.options.sampleRate > 0 && Math.random() < this.options.sampleRate;
  }

  isThemeEditor() {
    return Boolean(window.Shopify?.designMode);
  }

  shouldStart() {
    if (this.options.disableInThemeEditor && this.isThemeEditor()) return false;

    return this.isSamplingEnabled();
  }

  start() {
    if (this.started) return;

    if (!this.shouldStart()) {
      this.debugLog('helper skipped', {
        disableInThemeEditor: this.options.disableInThemeEditor,
        isThemeEditor: this.isThemeEditor(),
        sampleRate: this.options.sampleRate,
      });

      return;
    }

    this.observeCWV();
    this.observeLongTasks();
    this.captureNavigationMetrics();
    this.setupLifecycleFlush();

    this.flushTimer = window.setInterval(() => {
      this.flush('interval');
    }, this.options.flushIntervalMs);

    this.started = true;
    this.debugLog('helper started');
  }

  stop() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];

    if (this.flushTimer) {
      window.clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.lifecycleAbortController) {
      this.lifecycleAbortController.abort();
      this.lifecycleAbortController = null;
    }

    this.started = false;
    this.debugLog('helper stopped');
  }

  observeCWV() {
    this.observeEntry(CWV_METRIC_TYPES.LCP, (entry) => {
      this.pageMetrics.LCP = formatTime(entry.startTime);

      this.attachToNode(entry.element, {
        metric: 'LCP',
        value: this.pageMetrics.LCP,
      });
    });

    this.observeEntry(CWV_METRIC_TYPES.CLS, (entry) => {
      if (entry.hadRecentInput) return;

      const shiftValue = entry.value || 0;

      this.pageMetrics.CLS = Number((this.pageMetrics.CLS + shiftValue).toFixed(4));

      entry.sources?.forEach((source) => {
        this.attachToNode(source.node, {
          metric: 'CLS',
          value: Number(shiftValue.toFixed(4)),
        });
      });
    });

    this.observeEntry(CWV_METRIC_TYPES.FCP, (entry) => {
      if (entry.name !== 'first-contentful-paint') return;

      this.pageMetrics.FCP = formatTime(entry.startTime);
      this.attachToNode(document.body, {
        metric: 'FCP',
        value: this.pageMetrics.FCP,
      });
    });

    this.observeEntry(CWV_METRIC_TYPES.INP, (entry) => {
      const eventDuration = entry.duration || 0;

      if (!this.pageMetrics.INP || eventDuration > this.pageMetrics.INP) {
        this.pageMetrics.INP = formatTime(eventDuration);
      }

      this.attachToNode(entry.target, {
        metric: 'INP',
        value: formatTime(eventDuration),
      });
    });
  }

  observeLongTasks() {
    this.observeEntry('longtask', (entry) => {
      this.pageMetrics.longTasksCount += 1;
      this.pageMetrics.longTasksDuration = formatTime(this.pageMetrics.longTasksDuration + entry.duration);

      this.attachToNode(document.body, {
        metric: 'LONG_TASK',
        value: formatTime(entry.duration),
      });
    });
  }

  captureNavigationMetrics() {
    const [navigationEntry] = performance.getEntriesByType('navigation');

    if (!navigationEntry) return;

    this.pageMetrics.TTFB = formatTime(navigationEntry.responseStart - navigationEntry.requestStart);
  }

  observeEntry(type, callback) {
    if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => callback(entry));
    });

    observer.observe({
      type,
      buffered: true,
      durationThreshold: type === 'event' ? 40 : undefined,
    });

    this.observers.push(observer);
  }

  setupLifecycleFlush() {
    if (this.lifecycleAbortController) {
      this.lifecycleAbortController.abort();
    }

    this.lifecycleAbortController = new AbortController();

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') {
          this.flush('visibilitychange');
        }
      },
      { signal: this.lifecycleAbortController.signal }
    );

    window.addEventListener(
      'pagehide',
      () => {
        this.flush('pagehide');
      },
      { signal: this.lifecycleAbortController.signal }
    );
  }

  trackSection(sectionElement, context = {}) {
    const sectionId = getShopifySectionId(sectionElement);

    if (!sectionId) return null;

    const nowTime = now();
    const key = getSectionMetricsKey(sectionId);

    if (!this.sectionMetrics.has(key)) {
      this.sectionMetrics.set(key, {
        name: context.name || 'section',
        sectionId,
        mounts: 0,
        totalMountTime: 0,
        totalInteractions: 0,
        metrics: {},
      });
    }

    const sectionData = this.sectionMetrics.get(key);

    if (context.name && sectionData.name === 'section') {
      sectionData.name = context.name;
    }

    sectionData.mounts += 1;
    sectionData.lastMountAt = formatTime(nowTime);

    return {
      key,
      endMount: () => {
        const duration = now() - nowTime;

        sectionData.totalMountTime = formatTime(sectionData.totalMountTime + duration);
      },
      recordInteraction: (name, duration) => {
        sectionData.totalInteractions += 1;

        sectionData.metrics[name] = formatTime((sectionData.metrics[name] || 0) + duration);
      },
    };
  }

  trackBlockStart(blockElement, name = 'interaction') {
    const sectionId = getShopifySectionId(blockElement);
    const blockId = getBlockId(blockElement);

    if (!sectionId || !blockId) return null;

    this.cleanupStaleInteractions();

    const token = `${sectionId}:${blockId}:${name}:${now()}`;

    this.interactions.set(token, {
      startedAt: now(),
      sectionId,
      blockId,
      name,
    });

    return token;
  }

  trackBlockEnd(token, meta = {}) {
    if (!token || !this.interactions.has(token)) return;

    const payload = this.interactions.get(token);

    this.interactions.delete(token);

    const duration = now() - payload.startedAt;
    const key = `${payload.sectionId}:${payload.blockId}`;

    if (!this.blockMetrics.has(key)) {
      this.blockMetrics.set(key, {
        sectionId: payload.sectionId,
        blockId: payload.blockId,
        interactions: {},
      });
    }

    const blockData = this.blockMetrics.get(key);

    blockData.interactions[payload.name] = blockData.interactions[payload.name] || {
      count: 0,
      totalDuration: 0,
      maxDuration: 0,
      ...meta,
    };

    const interaction = blockData.interactions[payload.name];

    interaction.count += 1;
    interaction.totalDuration = formatTime(interaction.totalDuration + duration);
    interaction.maxDuration = Math.max(interaction.maxDuration, formatTime(duration));

    this.attachToSection(payload.sectionId, {
      metric: `BLOCK_${payload.name.toUpperCase()}`,
      value: formatTime(duration),
    });
  }

  attachToNode(element, payload) {
    const sectionId = getShopifySectionId(element);
    const blockId = getBlockId(element);

    if (sectionId) {
      this.attachToSection(sectionId, payload);
    }

    if (sectionId && blockId) {
      const key = `${sectionId}:${blockId}`;

      if (!this.blockMetrics.has(key)) {
        this.blockMetrics.set(key, {
          sectionId,
          blockId,
          interactions: {},
          cwv: {},
        });
      }

      const blockData = this.blockMetrics.get(key);

      if (!blockData.cwv) blockData.cwv = {};

      const metricKey = payload.metric;

      if (!blockData.cwv[metricKey]) {
        blockData.cwv[metricKey] = {
          count: 0,
          total: 0,
          max: 0,
        };
      }

      blockData.cwv[metricKey].count += 1;
      blockData.cwv[metricKey].total = formatTime(blockData.cwv[metricKey].total + payload.value);
      blockData.cwv[metricKey].max = Math.max(blockData.cwv[metricKey].max, payload.value);
    }
  }

  attachToSection(sectionId, payload) {
    const key = getSectionMetricsKey(sectionId);

    if (!this.sectionMetrics.has(key)) {
      this.sectionMetrics.set(key, {
        name: 'section',
        sectionId,
        mounts: 0,
        totalMountTime: 0,
        totalInteractions: 0,
        metrics: {},
      });
    }

    const sectionData = this.sectionMetrics.get(key);

    if (!sectionData.metrics[payload.metric]) {
      sectionData.metrics[payload.metric] = {
        count: 0,
        total: 0,
        max: 0,
      };
    }

    const metric = sectionData.metrics[payload.metric];

    metric.count += 1;
    metric.total = formatTime(metric.total + payload.value);
    metric.max = Math.max(metric.max, payload.value);
  }

  cleanupStaleInteractions(referenceTime = now()) {
    this.interactions.forEach((interaction, token) => {
      if (referenceTime - interaction.startedAt > this.options.interactionTtlMs) {
        this.interactions.delete(token);
      }
    });
  }

  flush(reason = 'manual') {
    this.cleanupStaleInteractions();

    const snapshot = {
      timestamp: new Date().toISOString(),
      reason,
      page: cloneSnapshot(this.pageMetrics),
      sections: cloneSnapshot(Array.from(this.sectionMetrics.values())),
      blocks: cloneSnapshot(Array.from(this.blockMetrics.values())),
    };

    if (this.started) {
      globalThis[this.options.namespace] = snapshot;
    }

    this.debugLog('flush', snapshot);

    return snapshot;
  }

  debugLog(message, payload) {
    if (!this.options.debug) return;

    if (payload === undefined) {
      console.log('[performance-helper]', message);

      return;
    }

    console.log('[performance-helper]', message, payload);
  }
}

let singleton;

export const getPerformanceObserverHelper = (options = {}) => {
  if (!singleton) {
    singleton = new PerformanceObserverHelper(options);

    return singleton;
  }

  singleton.configure(options);

  return singleton;
};
